import { AbstractControl, ValidationErrors } from '@angular/forms';

/**
 * Decimal entry for sample depth and rock percentage.
 *
 * These are typed by agents on machines set to South African regional settings, where the
 * decimal separator is a comma. `<input type="number">` is localised by the browser: on such a
 * machine a typed full stop is not a valid separator, so the control value becomes null and the
 * box blanks the moment it re-renders, for instance when the Add Group dialog opens over it. The
 * sample is then saved with no depth or rock percentage at all and nothing says so.
 *
 * So the inputs are `type="text" inputmode="decimal"`, both separators are accepted, and a comma
 * is what we render. What goes to the API is always a plain number: these land in Postgres
 * `numeric` columns, which reject "2,5" outright.
 *
 * Same rule as `irrigation-rate.ts`, which solved this for mm per hour. Kept separate because
 * that one carries its own range limit and these two fields have none.
 */

/**
 * What the Pin Management API returns for a depth or rock percentage that was never captured.
 * Five mappers do it, including the mobile sync, so it arrives on every read path.
 */
const NOT_CAPTURED = -1;

export type ParsedDecimal = number | null | 'invalid';

/**
 * What the user typed: null when empty, 'invalid' when it is not a number. Accepts 2,5 and 2.5.
 *
 * Negatives are invalid. Neither field can be negative, and refusing them keeps the API's -1
 * sentinel from being typed back in by hand and stored as a real reading.
 */
export function parseSampleDecimal(text: string | number | null | undefined): ParsedDecimal {
    if (text === null || text === undefined) return null;

    // Trimmed at the ends only, where irrigation-rate.ts strips every space so that a grouped
    // "1 234,56" reads as a rate. Deliberately not copied: rock percentage runs 0 to 100 and
    // depth is centimetres, so neither ever needs a thousands separator, and stripping would
    // turn a fat-fingered "2 5" into 25 instead of flagging it.
    const trimmed = String(text).trim();
    if (trimmed === '') return null;
    if (!/^\d+([.,]\d+)?$/.test(trimmed)) return 'invalid';

    const value = Number(trimmed.replace(',', '.'));
    return isFinite(value) ? value : 'invalid';
}

/**
 * For an input box: decimal comma, and empty for both null and the API's -1.
 *
 * Takes a string as well as a number because the two side panels hold these values as whatever
 * the box last contained, and a value that has been round-tripped through the form is already
 * comma-separated.
 */
export function sampleDecimalText(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') return '';

    const parsed = Number(String(value).replace(',', '.'));
    if (!isFinite(parsed) || parsed === NOT_CAPTURED) return '';

    return String(parsed).replace('.', ',');
}

/**
 * The number to send, or null. Returns null for 'invalid' as well, so callers must check with
 * {@link firstInvalidDecimal} or {@link parseSampleDecimal} before calling this. Silently
 * dropping a typo is exactly the failure this file exists to stop.
 */
export function sampleDecimalValue(text: string | number | null | undefined): number | null {
    const parsed = parseSampleDecimal(text);
    return typeof parsed === 'number' ? parsed : null;
}

/**
 * The label of the first field that is not a number, or null when they are all usable. For the
 * screens that drive these fields with ngModel and have no form validation to lean on.
 */
export function firstInvalidDecimal(
    fields: { label: string; text: string | number | null | undefined }[]
): string | null {
    const bad = fields.find(f => parseSampleDecimal(f.text) === 'invalid');
    return bad ? bad.label : null;
}

/** Reactive forms version, so the existing `[disabled]="form.invalid"` bindings do the work. */
export function sampleDecimalValidator(control: AbstractControl): ValidationErrors | null {
    return parseSampleDecimal(control.value) === 'invalid' ? { sampleDecimal: true } : null;
}
